import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useGooglePlacesAutocomplete } from './hooks';
import { FC } from 'react';
import { isNil } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { useJsApiLoader } from '@react-google-maps/api';
import SearchIcon from '@mui/icons-material/Search';
import { GOOGLE_LIBRARIES } from '@common/types/constants';

interface IGooglePlacesAutocompleteProps {
  error?: boolean;
  placeId: string;
  onChange: (place: Partial<Partial<google.maps.places.Place>>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

const GooglePlacesAutocompleteComponent: FC<IGooglePlacesAutocompleteProps> = ({
  error = false,
  placeId,
  onChange,
  placeholder = 'Search a place',
  required = false,
  disabled = false,
}) => {
  const {
    handleSelect,
    options,
    selectedOption,
    setSelectedOption,
    inputValue,
    setInputValue,
    dummyMapRef,
  } = useGooglePlacesAutocomplete({ onChange, placeId });

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        disabled={disabled}
        getOptionLabel={option => option.placePrediction?.text.text || ''}
        filterOptions={x => x}
        options={options}
        autoComplete
        includeInputInList
        noOptionsText={'No options'}
        onInputChange={(event, newInputValue) => {
          if (event && event.type === 'change') {
            setInputValue(newInputValue);
          }
        }}
        onBlur={() => {
          if (selectedOption) {
            setInputValue(selectedOption.placePrediction?.text?.text ?? '');
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            sx={{
              bgcolor: 'white',
            }}
            label={placeholder}
            fullWidth
            error={error}
            required={required}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment sx={{ mr: 0 }} position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <Box
                    sx={{
                      '.MuiAutocomplete-endAdornment': {
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                      },
                    }}
                  >
                    {params.slotProps.input?.endAdornment}
                  </Box>
                ),
              },
            }}
          />
        )}
        onChange={(event, value) => {
          if (value && value.placePrediction?.placeId) {
            setSelectedOption(value);
            handleSelect(value?.placePrediction?.placeId);
          }
        }}
        slotProps={{
          clearIndicator: {
            sx: {
              display: inputValue ? 'flex' : 'none',
            },
            onClick: (e: { stopPropagation: () => void }) => {
              e.stopPropagation();
              setInputValue('');
              setSelectedOption(null);
              handleSelect('');
            },
          },
        }}
        value={selectedOption ?? null}
        inputValue={inputValue ?? null}
        isOptionEqualToValue={(option, value) =>
          option.placePrediction?.placeId === value.placePrediction?.placeId
        }
      />
      <div ref={dummyMapRef} style={{ display: 'none' }} />
    </Box>
  );
};

// const API_KEY: string = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
const API_KEY = 'AIzaSyAej7D-n4nhZSEhhQlvDm7nwGoYUmdIT48';

export const GooglePlacesLoader = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: GOOGLE_LIBRARIES,
  });

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading...</div>;

  return <>{children}</>;
};

//This wrapper ensures that the API script is loaded before rendering
export const GooglePlacesAutocomplete = (
  props: IGooglePlacesAutocompleteProps,
) => {
  return (
    <GooglePlacesLoader>
      <GooglePlacesAutocompleteComponent {...props} />
    </GooglePlacesLoader>
  );
};

interface IFormGooglePlacesAutocomplete {
  name?: string;
  label?: string;
  placeId?: string;
  onChange: (place: Partial<google.maps.places.Place>) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
}

//Wrapper over the basic component to make form usage easier
export const FormGooglePlacesAutocomplete: FC<
  IFormGooglePlacesAutocomplete
> = ({
  name = 'google_places_id',
  label = '',
  placeholder = 'Search a place',
  disabled = false,
  onChange,
  required,
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      disabled={disabled}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormControl error={!isNil(error)} fullWidth>
            {label && (
              <FormLabel sx={{ width: '100%', pb: '1rem' }} id={name}>
                {label}
              </FormLabel>
            )}

            <GooglePlacesAutocomplete
              error={!isNil(error)}
              placeId={field.value}
              onChange={onChange}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
            />

            <FormHelperText error={!isNil(error)}>
              {error ? error.message : null}
            </FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};
